import { Link } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";

import React, { useState } from "react";
import { useAuth } from "./AuthContext.tsx";
import { TextInput } from "./inputs/TextInput.tsx";
import { Button } from "./widgets/Button.tsx";

interface LoginProps {
  isSigningUp: boolean;
  setIsSigningUp: (value: boolean) => void;
}

interface LoginValidationErrors {
  email?: string;
  password?: string;
}

const Login: React.FC<LoginProps> = (props) => {
  const { login, loginWithGoogle, signup } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { isSigningUp, setIsSigningUp } = props;
  const [validationErrors, setValidationErrors] =
    useState<LoginValidationErrors>({});

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (validationErrors.email) {
      setValidationErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (validationErrors.password) {
      setValidationErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  const validateSignInInputs = (): boolean => {
    const errors: LoginValidationErrors = {};
    if (!email) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Not a valid email.";
    }
    if (!password) {
      errors.password = "Password is required.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSignUpInputs = (): boolean => {
    const errors: LoginValidationErrors = {};
    if (!email) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Not a valid email.";
    }
    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <div className="bg-gray-100 flex flex-col gap-2 p-6 rounded shadow-lg w-full max-w-96 mx-2">
      <Button
        variant="primary"
        className="mb-2 flex items-center justify-center gap-2"
        onClick={loginWithGoogle}
      >
        <FcGoogle size={20} />
        Continue with Google
      </Button>
      <div className="flex items-center my-2">
        <hr className="flex-grow border-gray-300" />
        <span className="px-4 text-gray-500">or</span>
        <hr className="flex-grow border-gray-300" />
      </div>

      <TextInput
        title="Email"
        placeholder="Email"
        type="email"
        value={email}
        onChange={handleEmailChange}
        errorText={validationErrors.email}
        titleShade="dark"
      />
      <TextInput
        title="Password"
        type="password"
        placeholder="Password"
        value={password}
        onChange={handlePasswordChange}
        errorText={validationErrors.password}
        titleShade="dark"
      />

      <Button
        className="my-2"
        variant="submit"
        onClick={() => {
          if (isSigningUp) {
            if (validateSignUpInputs()) {
              signup(email, password).catch((error) => {
                alert("Sign up failed: " + error.message);
              });
            }
          } else {
            if (validateSignInInputs()) {
              login(email, password).catch((error) => {
                alert("Login failed: " + error.message);
              });
            }
          }
        }}
      >
        {isSigningUp ? "Sign Up" : "Sign In"}
      </Button>

      {isSigningUp ? (
        <p className="text-center text-sm my-2">
          <button
            onClick={() => setIsSigningUp(false)}
            className="text-info hover:text-info-dark underline"
          >
            Already have an account?
          </button>
        </p>
      ) : (
        <div className="flex justify-between text-sm m-2">
          <Link
            to="/forgot"
            className="text-info hover:text-info-dark underline"
          >
            Forgot password?
          </Link>
          <button
            onClick={() => setIsSigningUp(true)}
            className="text-info hover:text-info-dark underline"
          >
            Need an account?
          </button>
        </div>
      )}
    </div>
  );
};

export { Login };
